import { Empty } from "antd";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableFooter from "components/common/advance-table/AdvanceTableFooter";
import AdvanceTableSearchBox from "components/common/advance-table/AdvanceTableSearchBox";
import CardDropdown from "components/common/CardDropdown";
import { useAdvanceTable } from "hooks";
import useGuestDonations from "hooks/donations/useGuestDonations";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import React, { useState, useEffect, useCallback } from "react";
import { Badge, Col, Row, Spinner, Form, Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";

const STATUS_STYLES = {
  ACTIVE: {
    bg: "#E8F7E3",
    text: "#2E7D32",
    border: "#D5EBD0",
    label: "ACTIVE",
  },
  PENDING: {
    bg: "#FFF4E5",
    text: "#ED6C02",
    border: "#FFE0B2",
    label: "PENDING",
  },
  FAILED: {
    bg: "#FDECEA",
    text: "#C62828",
    border: "#F5C6C3",
    label: "FAILED",
  },
};

const getPurposeLabel = (purpose) => {
  if (!purpose) return null;
  if (typeof purpose === "string") return purpose;
  return purpose.name || purpose.code || purpose.id || null;
};

const GuestDonationsTable = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const {
    donations,
    loading,
    error,
    totalPages,
    totalElements,
  } = useGuestDonations();

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatus("");
    setPage(0);
  };

  const columns = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ getValue }) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: 150 }}
          title={getValue()}
        >
          {getValue()}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "code",
      header: "Code",
      cell: ({ getValue }) => getValue() || "N/A",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "amount",
      header: "Amount (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "balance",
      header: "Balance (USD)",
      cell: ({ getValue }) =>
        getValue() ? `$${getValue().toFixed(2)}` : "$0.00",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const value = getValue();
        const status = STATUS_STYLES[value] || {
          bg: "#F3F4F6",
          text: "#374151",
          border: "#E5E7EB",
          label: value || "Unknown",
        };
        return (
          <span
            className="py-2"
            style={{
              backgroundColor: status.bg,
              color: status.text,
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "0.65rem",
              border: `1px solid ${status.border}`,
              cursor: "default",
              fontWeight: 600,
            }}
          >
            {status.label}
          </span>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
        cellProps: { className: "fs-9" },
      },
    },
    {
      accessorKey: "currency",
      header: "Currency",
      cell: ({ getValue }) => getValue() || "USD",
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "purposes",
      header: "Purposes",
      cell: ({ getValue }) => {
        const labels = (getValue() || []).map(getPurposeLabel).filter(Boolean);
        if (!labels.length) return "N/A";

        const visible = labels.slice(0, 2);
        const hiddenCount = labels.length - visible.length;

        return (
          <div className="d-flex flex-wrap gap-1" style={{ maxWidth: 220 }}>
            {visible.map((label) => (
              <Badge key={label} bg="success" className="text-wrap">
                {label}
              </Badge>
            ))}
            {hiddenCount > 0 && (
              <Badge bg="secondary">+{hiddenCount}</Badge>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return "N/A";
        const date = new Date(value);
        if (isNaN(date.getTime())) return "Invalid Date";
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
      },
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "anonymous",
      header: "Anonymous",
      cell: ({ getValue }) => (getValue() ? "Yes" : "No"),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      accessorKey: "guestPaymentChargeId",
      header: "Payment ID",
      cell: ({ getValue }) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: 120 }}
          title={getValue()}
        >
          {getValue() || "N/A"}
        </span>
      ),
      meta: {
        headerProps: { className: "text-900" },
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <CardDropdown drop="start">
            <div className="py-2">
              <button
                className="dropdown-item btn-link text-decoration-none"
                onClick={() => navigate(`/olitask/donations/guest/${item.id}`)}
              >
                View details
              </button>
            </div>
          </CardDropdown>
        );
      },
      meta: {
        headerProps: { className: "text-900 text-center" },
        cellProps: { className: "text-center" },
      },
    },
  ];

  const table = useAdvanceTable({
    data: donations,
    columns,
    sortable: true,
    pagination: true,
    perPage: 20,
  });

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <WidgetSectionTitle
        icon="hands-helping"
        title="Guest Donations"
        subtitle="Manage and monitor guest donations"
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
        <Form.Control
          type="text"
          placeholder="Search by code..."
          value={search}
          onChange={handleSearch}
          style={{ maxWidth: 250 }}
          className="mb-0"
        />
        <Form.Select
          value={status}
          onChange={handleStatusChange}
          style={{ maxWidth: 180 }}
          className="mb-0"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="PENDING">Pending</option>
          <option value="FAILED">Failed</option>
        </Form.Select>
        {(search || status) && (
          <button
            className="btn btn-outline-secondary btn-sm mb-0"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        )}
      </div>

      {donations && donations.length > 0 ? (
        <AdvanceTableProvider {...table}>
          <Row className="mb-2">
            <Col md={4}>
              <AdvanceTableSearchBox />
            </Col>
          </Row>

          <AdvanceTable
            headerClassName="bg-200 text-nowrap align-middle"
            rowClassName="btn-reveal-trigger text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: "text-xs mb-0 overflow-hidden",
            }}
          />

          <div className="mt-3">
            <AdvanceTableFooter
              navButtons
              rowsPerPageSelection
              page={page}
              setPage={setPage}
              size={size}
              setSize={setSize}
              totalPages={totalPages}
              totalElements={totalElements}
            />
          </div>
        </AdvanceTableProvider>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 300 }}
        >
          <Empty description="No guest donations found" />
        </div>
      )}
    </>
  );
};

export default GuestDonationsTable;